import * as msal from "@azure/msal-browser";

interface MsalPluginOptions {
    clientId: string;
    redirectUri: string;
    b2cScope: string[];
    b2cPolicies: B2CPolicies;
}

interface B2CPolicies {
    names: {
        signUpSignIn: string;
        editProfile: string;
    };
    authorities: {
        signUpSignIn: {
            authority: string;
        };
        editProfile: {
            authority: string;
        };
    };
    authorityDomain: string;
}

export class MsalService {
    private pluginOptions: MsalPluginOptions = {
        clientId: import.meta.env.VITE_msalClientId || "",
        redirectUri: "",
        b2cPolicies: {
            names: {
                signUpSignIn: import.meta.env.VITE_signUpSignInPolicyName || "",
                editProfile: "",
            },
            authorities: {
                signUpSignIn: {
                    authority: import.meta.env.VITE_signUpSignInAuthority || "",
                },
                editProfile: {
                    authority: "",
                },
            },
            authorityDomain: import.meta.env.VITE_authorityDomain || "",
        },
        b2cScope: [import.meta.env.VITE_b2cScope || ""],
    };

    public isAuthenticated = false;
    private _accountId = "";
    username = "";
    emailaddress = "";
    apimKey = "";
    public accessToken: string = "";

    private _msalInstance: msal.PublicClientApplication | null = null;
    private _msalPluginInstance: MsalService | null = null;
    public constructor() {
        this.initialize(this.pluginOptions);
        this._msalPluginInstance = this;
    }
    private static _instance: MsalService | null = null;
    public static getInstance(): MsalService {
        if (this._instance == null) {
            this._instance = new MsalService();
        }
        return this._instance;
    }

    private initialize(options: MsalPluginOptions) {
        const msalConfig: msal.Configuration = {
            auth: {
                clientId: options.clientId,
                authority: options.b2cPolicies.authorities.signUpSignIn.authority,
                knownAuthorities: [options.b2cPolicies.authorityDomain],
                redirectUri: window.location.origin,
            },
            cache: {
                cacheLocation: "localStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
                storeAuthStateInCookie: false, // If you wish to store cache items in cookies as well as browser cache, set this to "true".
            },
            system: {
                loggerOptions: {
                    loggerCallback: (
                        level: msal.LogLevel,
                        message: string,
                        containsPii: boolean,
                    ): void => {
                        if (containsPii) {
                            return;
                        }
                        switch (level) {
                            case msal.LogLevel.Error:
                                console.error(message);
                                return;
                            case msal.LogLevel.Info:
                                console.info(message);
                                return;
                            case msal.LogLevel.Verbose:
                                console.debug(message);
                                return;
                            case msal.LogLevel.Warning:
                                console.warn(message);
                                return;
                        }
                    },
                    piiLoggingEnabled: false,
                    logLevel: msal.LogLevel.Verbose,
                },
            },
        };
        this._msalInstance = new msal.PublicClientApplication(msalConfig);
        this.isAuthenticated = this.getIsAuthenticated();

        console.log("In msal service initialize Authenticated value");
        console.log(this.isAuthenticated);
    }

    public async signIn() {
        try {
            const loginRequest: msal.PopupRequest = {
                scopes: ["openid", ...this.pluginOptions.b2cScope],
            };
            console.log("in sign in");
            let response = await this._msalInstance?.handleRedirectPromise();
            if (response !== null) {
                this.selectAccount();
            } else {
                // In case multiple accounts exist, you can select
                const currentAccounts = this._msalInstance?.getAllAccounts();

                if (currentAccounts!.length === 0) {
                    //Logging in app insights
                    console.log(
                        "HandleRedirect: no accounts signed-in, attempting to sign a user in",
                    );
                    // no accounts signed-in, attempt to sign a user in
                    await this._msalInstance?.loginRedirect(loginRequest);
                    this.isAuthenticated = false;
                } else {
                    this.selectAccount();
                }
            }
            this.isAuthenticated = this.getIsAuthenticated();
            console.log("Authenticated value" + this.isAuthenticated);
        } catch (err: any) {
            this.isAuthenticated = false;
        }
    }

    private setAccount(account: msal.AccountInfo | null) {
        if (account) {
            console.log("account", account);
            this._accountId = account.homeAccountId;
            this.username = account.username;
            if (account.idTokenClaims && account.idTokenClaims["email"]) {
                this.emailaddress = account.idTokenClaims["email"] as string;
            }
            if (account.idTokenClaims && account.idTokenClaims["apikey"]) {
                this.apimKey = account.idTokenClaims["apikey"] as string;
            }
        }
    }

    selectAccount() {
        /**
         * See here for more info on account retrieval:
         * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
         */

        const currentAccounts = this._msalInstance!.getAllAccounts();

        if (currentAccounts.length < 1) {
            console.log("account not found");
            return;
        } else if (currentAccounts.length > 1) {
            /**
             * Due to the way MSAL caches account objects, the auth response from initiating a user-flow
             * is cached as a new account, which results in more than one account in the cache. Here we make
             * sure we are selecting the account with homeAccountId that contains the sign-up/sign-in user-flow,
             * as this is the default flow the user initially signed-in with.
             */
            const accounts = currentAccounts.filter(
                (account) =>
                    account.homeAccountId
                        .toUpperCase()
                        .includes(
                            this.pluginOptions.b2cPolicies.names.signUpSignIn.toUpperCase(),
                        ) &&
                    account &&
                    account.idTokenClaims &&
                    account.idTokenClaims.iss &&
                    account.idTokenClaims.iss
                        .toUpperCase()
                        .includes(
                            this.pluginOptions.b2cPolicies.authorityDomain.toUpperCase(),
                        ) &&
                    account.idTokenClaims.aud === this.pluginOptions.clientId,
            );

            if (accounts.length > 1) {
                // localAccountId identifies the entity for which the token asserts information.
                if (
                    accounts.every(
                        (account) => account.localAccountId === accounts[0].localAccountId,
                    )
                ) {
                    // All accounts belong to the same user
                    this.setAccount(accounts[0]);
                } else {
                    // Multiple users detected. Logout all to be safe.
                    this.signOut();
                }
            } else if (accounts.length === 1) {
                this.setAccount(accounts[0]);
            }
        } else if (currentAccounts.length === 1) {
            this.setAccount(currentAccounts[0]);
        }
    }

    public async signOut() {
        let loggedInAccount = this._msalInstance!.getAccountByHomeId(
            this._accountId,
        );
        const logoutRequest = {
            account: loggedInAccount,
        };

        console.log("clearing local storage data");
        localStorage.clear();
        this.isAuthenticated = false;
        await this._msalInstance!.logoutRedirect(logoutRequest);
    }

    public async acquireToken() {
        const tokenRequest: msal.SilentRequest = {
            scopes: [...this.pluginOptions.b2cScope], // e.g. ["https://fabrikamb2c.onmicrosoft.com/helloapi/demo.read"]
            forceRefresh: false, // Set this to "true" to skip a cached token and go to the server to get a new token
        };
        const accountInfo = this._msalInstance!.getAccountByHomeId(this._accountId);
        if (accountInfo) {
            tokenRequest.account = accountInfo;
        } else {
            const loginRequest: msal.RedirectRequest = {
                scopes: ["openid", ...this.pluginOptions.b2cScope],
            };
            await this._msalInstance!.loginRedirect(loginRequest);
            return null;
        }

        try {
            const response = await this._msalInstance!.acquireTokenSilent(
                tokenRequest,
            );
            if (response?.accessToken) {
                this.accessToken = response.accessToken;
                return response;
            } else {
                this.accessToken = "";
                const idTokenEmptyError = new Error(
                    "Access token was empty when acquiring token silently",
                );
                throw idTokenEmptyError;
            }
        } catch (error) {
            console.log(error);
            try {
                await this._msalInstance!.acquireTokenRedirect(tokenRequest);
                return null;
            } catch (acquireTokenRedirectError) {
                console.error(acquireTokenRedirectError);
                throw acquireTokenRedirectError;
            }
        }
    }

    private getIsAuthenticated(): boolean {
        const accounts: msal.AccountInfo[] = this._msalInstance!.getAllAccounts();
        let hasAccount = accounts && accounts.length > 0;

        if (hasAccount && this.emailaddress) return true;

        return false;
    }

    public async HandleRedirect() {
        let response = await this._msalInstance?.handleRedirectPromise();
        if (response) {
            this.selectAccount();
            this.accessToken = response.accessToken;
        } else {
            // In case multiple accounts exist, you can select
            const currentAccounts = this._msalInstance?.getAllAccounts();
            console.log("currentAccount");
            if (currentAccounts!.length !== 0) {
                console.log(currentAccounts?.length);
                this.selectAccount();
            } else {
                console.log(currentAccounts?.length);
            }
        }
        this.isAuthenticated = this.getIsAuthenticated();
    }
}
